/**
 * Queries.jsx — Admin / KAM Queries module
 *
 * Renders the booking-query workspace: a filterable, paginated table of all
 * shipment queries plus the inline detail views (search form, manual quote,
 * booking detail) that open when a row is acted upon. Behaviour adapts to the
 * logged-in user's role (customer vs. KAM/admin).
 *
 * Author: FreightSee frontend team
 */
import { DatePicker, message, Modal, Spin, Tabs, Tooltip } from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import React, { useContext, useEffect, useRef, useState } from "react";
import "./Queries.css";
import { useDispatch, useSelector } from "react-redux";
import CustomeHeader from "../../components/Admin/Common/CustomeHeader";
import FilterDatePicker from "../../components/Admin/Common/FilterDatePicker";
import FilterSelect from "../../components/Admin/Common/FilterSelect";
import SimpleTable from "../../components/Admin/Common/SimpleTable";
import { create_hike_job, get_booking_history, get_single_search_query } from "../../store/Admin/Queries/QuerySlice";
import {
  buildAirSearchPayload,
  buildOceanResultsState,
  buildOceanSearchQuery,
} from "../../utils/openQueryResults";
// import {

//   resetSearchRatesState ,
// } from "../../store/SearchRates/SearchRatesSlice";
import { clearStoredSearch, updateStoredSearch, updatequeryid } from "../../store/Newsearchform/Newsearchform"
import { User } from 'lucide-react';
import { newqueries } from "../../store/Newsearchform/Newqueries"
import {
  LeftHead,
  StatusText,
  SubHeadContainer,
  SubHeadTitle,
  TablePagination,
  TitleHead,
} from "../../StyledComponents/Common";
import {
  ContentContainer,
  TableTitle,
  TableTop,
} from "../../StyledComponents/CreateKeyAccountManager";
import {
  MainContainer,
  NameSection,
  TopSection,
} from "../../StyledComponents/Dashboard";
import {
  DateSection,
  FilterSection,
  FlexContainer,
  LabelItem,
  RightSection,
  SpinContainer,
} from "../../StyledComponents/Queries";
import { IconButton } from "@mui/material";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import SearchBox from "../../components/Admin/Common/SearchBox";
import moment from "moment-timezone";
import { FcViewDetails } from "react-icons/fc";

import {
  Filter_QUERY,
  makePayloadForRateAvailability,
  QUERIES_CLASS,
} from "../../utils/constant";
import {
  HeaderContainer,
  HeadName,
  HeadShort,
  IconImg,
} from "../../StyledComponents/Header";
import FlightRate from "../../forms/SearchRates/FlightRate/FlightRate";
import { get_booking_markup } from "../../store/Markup/markupSlice";
import MarkupBookingBreakup from "./MarkupBookingBreakup";
// AirCargoDetails / OceanCargoDetails are no longer imported here: this page
// used to render them inline for a confirmed/cancelled booking. Every role now
// opens those at their own URL instead (see handleAirBookingView /
// handleOceanBookingView), so the components are reached through the router.
import BlDetailsEditor from "../../components/common/BlDetailsEditor";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ManageSocketContext } from "../../ManageSocketContext";
import {
  getAllManualQuoteById,
  getManualQuoteDetailById,
  setQueryDetail,
  setManualQuoteDetail,
} from "../../store/ManualQuote/ManualQuoteSlice";
import {
  setOceanQueryDetail,
  getOceanManualQuoteDetailById,
  setOceanManualQuoteDetail,
} from "../../store/ManualQuote/OceanManualQuoteSlice";
import { updatenotificationdata } from "./../../store/Admin/Common/CommonSlice";
import { getQuery, searchOceanRate } from "../../store/SearchRates/SearchRatesSlice";
import { savequeryid } from "../../store/Queries/QueriesSlice";

import NewsearchForm from "../../forms/SearchRates/FlightRate/Newsearchform/NewsearchForm";
import ShipmentRateForm from "../../forms/SearchRates/ShipmentRate/ShipmentRateForm";
import NewRateAvailableList from "../../components/SearchRates/NewRateAvailableList";
import { totalContainerWeight } from "../../utils/oceanContainerWeight";
import { isQueryLimitReached } from "../../utils/queryLimit";
import VerifyEmailModal from "../../components/common/VerifyEmailModal";

const USER_ROLES = ["customer", "customer_admin"];
let debounceTimer = null;

// The booking-history table row is a summary projection (Origin/Destination/
// Incoterms/Container Details/... — only what the list columns need); the
// full saved query (search_cargo_result_detail) additionally carries things
// like the Service Provider selection, currency and stuffing. Layers `full`
// over `prev` so the view/edit form gets everything, without letting a key
// the full document happens not to carry (e.g. Quote_status, userDetails)
// blank out a value the summary row already had.
const mergeFullQueryDetail = (prev, full) => {
  if (!full) return prev;
  const merged = { ...prev };
  Object.keys(full).forEach((key) => {
    if (full[key] !== undefined) merged[key] = full[key];
  });
  return merged;
};

/**
 * Ocean ports arrive as a display string — "Mundra - (INMUN1)" — while the
 * table column only wants the UN/LOCODE. Pulls the code out of the trailing
 * parentheses, falling back to the whole (trimmed) string for a value that's
 * already bare, e.g. "INNSA".
 *
 * @param {string} [port] - The port value off the booking-history row.
 * @returns {string} The port code, or "—" when there's nothing to show.
 */
const extractPortCode = (port) => {
  // Coerce first: a non-string value (a number, say) has no .match, and a
  // whitespace-only string trims to "" — which is not nullish, so a trailing
  // `?? "—"` would never catch it and the cell would render blank.
  const text = String(port ?? "").trim();
  if (!text) return "—";
  return text.match(/\(([^)]+)\)/)?.[1] ?? text;
};

/**
 * Renders the first `max` items comma-joined, followed by a "+N" chip whose
 * hover tooltip lists only the remaining items — keeps the narrow ocean
 * columns (Load Type, Container Details › Type) to a single tidy line.
 *
 * @param {string[]} items - Labels to show, in display order.
 * @param {number} [max=2] - How many to show before collapsing into the chip.
 */
const CollapsedList = ({ items, max = 2 }) => {
  if (!items.length) return "—";
  const hidden = items.slice(max);
  return (
    <span className="bq-collapsed-list">
      {items.slice(0, max).join(", ")}
      {hidden.length > 0 && (
        <Tooltip
          title={hidden.map((item, i) => (
            // Each item stays whole; the space sits outside the span, so the
            // tooltip only wraps between items.
            <React.Fragment key={i}>
              <span style={{ whiteSpace: "nowrap" }}>
                {item}{i < hidden.length - 1 ? "," : ""}
              </span>
              {i < hidden.length - 1 ? " " : ""}
            </React.Fragment>
          ))}
        >
          <span className="bq-collapsed-more">+{hidden.length}</span>
        </Tooltip>
      )}
    </span>
  );
};

/**
 * Builds the list of page numbers to render in the pagination control,
 * collapsing long ranges with "…" so only the first, last and pages around
 * the current one are shown.
 *
 * @param {number} current - The currently active page (1-based).
 * @param {number} total   - Total number of pages available.
 * @returns {Array<number|string>} Ordered page tokens, e.g. [1, "…", 4, 5, 6, "…", 20].
 */
function buildPageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  const add = (n) => { if (pages[pages.length - 1] !== n) pages.push(n); };
  add(1);
  if (current > 3) add("…");
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) add(i);
  if (current < total - 2) add("…");
  add(total);
  return pages;
}

function Queries() {
  const dispatch = useDispatch();
  const [searchparams] = useSearchParams();
  const params = searchparams.get("queryId");
  const quoteType = searchparams.get("quoteType")
  const navigate = useNavigate();
  const location = useLocation();
  const userroles = useSelector((state) => state.data?.data?.data?.user?.userroles);
  const currentUser = useSelector((state) => state.data?.data?.data?.user);
  // Verify-email gate for opening an ocean query's rates (see handleSeachFormdata).
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const pendingOceanOpenRef = useRef(null);
  const { setReceivingResponse } = useContext(ManageSocketContext);
  const [pageType, setPageType] = useState("table");
  // True from the moment a "Quote Available" row is opened until the navigation
  // to its results page happens. Drives the loader that replaces this page's
  // body, so no query-detail / search form is ever painted on the way out.
  const [openingRates, setOpeningRates] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [instance, setInstance] = useState({});
  const newsearchFormRef = useRef(null);

  const [data, setData] = useState([]);
  const [totalData, setTotalData] = useState(0)
  const [markup_detail, setMarkup_detail] = useState({});
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({});
  const [searchQueryData, setSearchQueryData] = useState({});

  const [manualQuoteQueryData, setManualQuoteQueryData] = useState(null);
  // Ocean "Booking Confirmed" documents popup (KAM/admin only) — holds the row
  // whose file icon was clicked; null keeps the modal closed. The modal is
  // deliberately dismissable only via its own Cancel button (no mask click, no
  // Esc, no × ), so nothing here reacts to an outside click.
  const [oceanDocRecord, setOceanDocRecord] = useState(null);
  // Start collapsed: the Filters & Search panel should stay closed when the page
  // loads and only open when the user clicks the down-arrow chevron.
  const [filterOpen, setFilterOpen] = useState(false);
  // Air/Ocean tab switch for the queries table, mirroring the user-side
  // booking-history tabs (1 = Air Cargo, 2 = Ocean Cargo). Defaults to Air,
  // but honors ?cargoTab=2 so a redirect (e.g. after submitting an ocean
  // manual quote — see OceanManualQuote's onSubmit) can land on the Ocean
  // tab instead of always bouncing back to Air.
  const [cargoTab, setCargoTab] = useState(searchparams.get("cargoTab") === "2" ? 2 : 1);
  const getCargoTypeParam = () => (cargoTab === 2 ? "Ocean Cargo" : "Air Cargo");

  // Fetches the booking-history list from the backend and populates the table.
  // `search` is an optional query string / filter payload; the response is
  // expected as { data: { data: [...rows], count: <total> } }.
  const get_booking_history_air = async (search = "") => {
    dispatch(get_booking_history(search))
      .then((res) => {
        setData(res?.payload?.data?.data);
        setTotalData(res?.payload?.data?.count);
        setLoading(false);
        // setCurrentPage(1);
      })
      .catch((err) => {
        console.error(err);
      });
  };
  useEffect(() => {
    // dispatch(resetSearchRatesState());
    dispatch(clearStoredSearch());
  }, []);


  useEffect(() => {
    if (pageType === "table") {
      console.log("pageType", pageType);
      get_booking_history_air({ ...state, cargoType: getCargoTypeParam() });
    }
  }, [pageType, cargoTab]);

  useEffect(() => {
    if (params != null) {
      dispatch(get_single_search_query(params)).then((res) => {
        const nodeData = res?.payload?.data?.nodeData;
        setSearchQueryData(nodeData);
        dispatch(updateStoredSearch({ nodeData }));
      })
        .catch((err) => {
          console.error(err);
        });
      getManualquoteQueryDetail(params);
      setPageType("Manual Quoted");
    }
  }, [params]);

  // Clicking the top-nav "Queries" link while already on this page/route
  // doesn't remount the component (same path) or clear whatever query detail
  // was left open (Query Detail / edit screen) — so a fresh navigation here
  // must explicitly snap back to the table view (mirrors handleBack), unless
  // it's a `?queryId=` deep link, which the effect above already handles.
  useEffect(() => {
    if (params == null) {
      setPageType("table");
      setSearchQueryData({});
      setIsEditMode(false);
      setManualQuoteQueryData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setState(prev => ({ ...prev, page, limit: pageSize }))
  };

  const handlePageSizeChange = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
    setState(prev => ({ ...prev, page: 1, limit: size }));
  };

  const handleFilterChange = (value) => {
    console.log(value, "value");

    //   setState((prevState) => {
    //     return {
    //       ...prevState,
    //       filter_value: "Query_status",
    //       status: value,
    //     };
    //   });
  };

  function filterByDateRange(fromDate, toDate) {
    const from_date = new Date(fromDate);
    const to_date = new Date(toDate);
    const from = new Date(
      from_date.getFullYear(),
      from_date.getMonth(),
      from_date.getDate()
    );
    const to = new Date(
      to_date.getFullYear(),
      to_date.getMonth(),
      to_date.getDate()
    );
    return {
      from,
      to,
    };
  }
  console.log(state);

  useEffect(() => {
    if (Object.values(state).length) {
      handleSearch()
    }
  }, [state]);

  const handleSearch = () => {
    const dataQuery = { ...state, cargoType: getCargoTypeParam() };

    if (dataQuery.departure_date) {
      dataQuery.departure_date = moment(new Date(dataQuery.departure_date)).format("YYYY-MM-DD");
    }
    if (dataQuery.booking_date) {
      dataQuery.booking_date = moment(new Date(dataQuery.booking_date)).format("YYYY-MM-DD");
    }

    get_booking_history_air(dataQuery);
  };

  const handledetails = (record) => {
    // dispatch(savequeryid(record.queryId));
    navigate(`/booking-view?queryId=${record?._id}`);

  };

  const columns = [
    {
      dataIndex: 'slNo',
      render: (text, record, index) => {
        return <span>{(index + 1) + ((currentPage - 1) * pageSize)}</span>;
      },
    },
    // {
    //   dataIndex: "userDetails",
    //   render: (text, record, index) => {
    //     console.log(record, "record");
    //     return (
    //       <>

    //         <div className="flex dataname">
    //           <i className="bg-[#ffebbc] rounded-lg p-[3px] text-center w-[30px] h-[30px]">
    //             <User className="w-[21px] text-yellow-700 inline" /></i>
    //           <aside className="ml-2 leading-[15px]">
    //             <span className="w-[180px] block whitespace-normal uppercase text-bold text-black">


    //               {userroles === "air_admin"
    //                 ? `${record?.kamDetails?.full_name} , `
    //                 : null}
    //               {record?.userDetails?.full_name}
    //             </span>
    //             <small className="text-[13px] block">
    //               {record?.organizationDetails?.org_name}</small>{" "}
    //           </aside>
    //         </div>
    //       </>
    //     )
    //   }
    // },
    // {
    //   // title: "originPort",
    //   dataIndex: "originPort",
    //   render: (text, record, index) => {
    //         const originAirport =
    //   record?.originPort?.originAirport
    //     ?.split("-")?.[1]
    //     ?.replace(/[()]/g, "");

    // const destinationAirport =
    //   record?.destinationPort?.destinationAirport
    //     ?.split("-")?.[1]
    //     ?.replace(/[()]/g, "");
    //     return (
    //       <>
    //         <div className="flex">
    //           <i className="mt-0 bg-[#ede7e7] p-[7px]  rounded-lg w-[30-px]"><img src="https://staging.sgate.in/html/designer/freightsee/flight.png" className="w-[15px]" alt="" /></i>

    //           <aside className="ml-2 leading-[14px]"><span>
    //             {/* {record?.originPort?.originAirport?.split("-")?.[1]?.replace(/[()]/g, "")}{" "} */}
    //             {/* {`${record?.originPort?.origincountryName
    //                 ? `(${record?.originPort?.origincountryName})`
    //                 : ""
    //               }`}{" "} */}
    //            {   originAirport}
    //             to{" "}
    //             {destinationAirport}
    //             {/* {record?.destinationPort?.destinationAirport
    //               ?.split("-")?.[1]
    //               ?.replace(/[()]/g, "")}{" "} */}
    //             {/* {`${record?.destinationPort?.destinationcountryName
    //                 ? `(${record?.destinationPort?.destinationcountryName})`
    //                 : ""
    //               }`} */}
    //           </span>
    //             {/* <span className="block text-gray-600 text-xs">{record?.goodsDetails?.commonditytype?.commonditytype}</span> */}
    //           </aside>
    //         </div>

    //       </>
    //     );
    //   },
    // },
    // {
    //   dataIndex: "goodsDetails",
    //   render: (text, record, index) => {
    //     return (
    //       <span>{record?.goodsDetails?.commonditytype?.commonditytype}</span>
    //     );
    //   },
    // },
    {
      title: "ROUTE",
      key: "route",
      render: (_, record) => {
        const originAirport =
          record?.originPort?.originAirport
            ?.split("-")?.[1]
            ?.replace(/[()]/g, "");

        const destinationAirport =
          record?.destinationPort?.destinationAirport
            ?.split("-")?.[1]
            ?.replace(/[()]/g, "");

        return (
          <div className="flex items-start">
            <i className="mt-0 bg-[#ede7e7] p-[7px] rounded-lg w-[30px] h-[30px] flex items-center justify-center">
              <img
                src="https://staging.sgate.in/html/designer/freightsee/flight.png"
                className="w-[15px]"
                alt=""
              />
            </i>

            <aside className="ml-2 leading-[14px]">
              <span className="block">
                {originAirport}
                {record?.originPort?.origincountryName &&
                  ` (${record.originPort.origincountryName})`}
                {" to "}
                {destinationAirport}
                {record?.destinationPort?.destinationcountryName &&
                  ` (${record.destinationPort.destinationcountryName})`}
              </span>
            </aside>
          </div>
        );
      },
    }
    ,
    {
      dataIndex: "packagingDetails",
      render: (text, record, index) => {
        console.log(record, "record");
        return (


          <span className="w-[125px] block">

            <p className="block"> {record?.packagingDetails?.packagingType?.totalunits}Pkts-
              {record?.packagingDetails?.packagingType?.totalweight}kg
            </p>
            <p className="block text-xs"> {record?.packagingDetails?.packagingType?.totalvolume}CBM</p>
          </span>
        );
      },
    },
    {
      dataIndex: "transportationRequired",
      render: (text, record, index) => {
        return (
          <span>
            {record?.originPort?.originPickup
              ? "Transportation Required"
              : record?.destinationPort?.destinationPickup
                ? "Transportation Required"
                : record?.originPort?.originPickup &&
                  record?.destinationPort?.destinationPickup
                  ? "Transportation Required"
                  : "N/A"}
          </span>
        );
      },
    },
    {
      title: () => (
        <FlexContainer>
          <span>STATUS</span>
        </FlexContainer>
      ),
      dataIndex: "Quote_status",
      render: (text, record) =>
        USER_ROLES?.includes(userroles) ? (
          text === "Auto Quoted" || text === "Manual Quoted" ? (
            <StatusText className="quoteavailable">Quote Available</StatusText>
          ) : text === "Quote Pending" ? (
            <StatusText className="quoteawaited">Quote Awaited</StatusText>
          ) : (
            "Booked"
          )
        ) : (
          <span className={QUERIES_CLASS[text]}>{text}</span>
        ),
    },

    {
      title: "Action",
      dataIndex: "action",
      render: (text, record) => (

        <div className="flex items-center gap-2">
          <IconButton onClick={() => handleSeachFormdata(record)}>
            <RemoveRedEyeOutlinedIcon style={{ cursor: "pointer" }} />
          </IconButton>
          {record?.Quote_status === "Booking Confirmed" &&
            <FcViewDetails className="flex w-8 h-8" onClick={() => handledetails(record)} />}
        </div>


      ),
    },
  ];

  const onSearch = (value) => {
    let queryStringObj = { cargoType: getCargoTypeParam() };
    if (value?.length > 0) {
      queryStringObj["search"] = value;
    }
    let queryString = new URLSearchParams(queryStringObj).toString();
    queryString = queryString ? `?${queryString}` : "";
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      get_booking_history_air(queryString);
    }, 700);
  };
  const handleBack = () => {
    setPageType("table");
    setSearchQueryData({});
    setIsEditMode(false);
    // Coming back from a results page must never land on the loader.
    setOpeningRates(false);
    // Prevent the next query opened from momentarily/permanently showing this
    // query's manually-quoted rates (see handleSeachFormdata/handleEditQuery).
    setManualQuoteQueryData(null);
    dispatch(clearStoredSearch());
  };

  const getManualquoteQueryDetail = (id) => {
    if (quoteType != undefined && quoteType != null && quoteType === "Quote") {
      return;
    }
    // if(userroles==="kam"){
    dispatch(getAllManualQuoteById(id)).then((res) => {
      let data = {
        message: res?.payload?.message,
        statusCode: res?.payload?.statusCode,
        success: res?.payload?.success,
      };
      if (
        res?.payload?.statusCode === 409 ||
        res?.payload?.statusCode === 400 ||
        res?.payload?.statusCode === 404
        || res?.payload?.statusCode === 500
      ) {
        dispatch(updatenotificationdata(data));
        return;
      }
      console.log(res?.payload, "res?.payload");
      const payloadRes = makePayloadForRateAvailability(res?.payload, id);
      if (payloadRes?.nodeData?.airlineResponse != null) {
        // API may return airlineResponse as a plain object {availabilityResponseSOs:[...]}
        // or as an array. Normalise to array so .map() works correctly.
        const airlineResponseArray = Array.isArray(payloadRes?.nodeData?.airlineResponse)
          ? payloadRes?.nodeData?.airlineResponse
          : [payloadRes?.nodeData?.airlineResponse];
        const updatedData = airlineResponseArray.map((item) => {
          let minRate = 0;
          item.availabilityResponseSOs?.map((data) => {
            minRate = Math.min(data?.rateDetails?.totalAmount, item.availabilityResponseSOs?.[0]?.rateDetails?.totalAmount);
            return data
          })
          return {
            ...item,
            minRate
          }
        })
        let payloadResponse = { ...payloadRes, nodeData: { ...payloadRes.nodeData } };
        payloadResponse.nodeData.airlineResponse = updatedData;
        setManualQuoteQueryData(payloadResponse);
      }


    });
    // }
  };

  // The booking-history row is a SUMMARY projection — it carries only what the
  // list columns render (origin/destination/status/weights/...), not the full
  // saved query. Every screen that opens the detail form from a row therefore
  // has to backfill the rest, or the KAM sees a form missing fields the
  // customer actually filled in (Service Provider selection, currency and
  // stuffing on ocean; goodsDetails/packagingDetails/preferred_airline and the
  // per-package shipment rows on air).
  //
  // The two cargo types read from different endpoints with different envelopes:
  //   Ocean  GET  search_cargo_result_detail/:id  -> data.nodeData
  //   Air    POST ratesearch/get-query            -> data.data  (raw Query doc)
  // Both come back with the same field names the forms read via
  // `searchquery.*`, so either result can be layered straight onto the row.
  //
  // Failures are deliberately swallowed: the summary row already renders a
  // usable form, so a missed backfill must degrade to "fewer fields", never to
  // a blanked-out form.
  //
  // Returns a promise resolving to the full document (or null), so a caller
  // that needs more than the form patch — the air rate-search payload below —
  // can build off the same in-flight request instead of firing a second one.
  const backfillFullQuery = (record) => {
    const id = record?._id;
    if (!id) return Promise.resolve(null);
    const isOcean = record?.cargoType === "Ocean Cargo";
    const request = isOcean
      ? dispatch(get_single_search_query(id))
      : dispatch(getQuery({ queryId: id }));
    return request
      .then((res) => {
        const full = isOcean
          ? res?.payload?.data?.nodeData
          : res?.payload?.data?.data;
        // mergeFullQueryDetail layers `full` over the row without letting a key
        // the full document happens not to carry (Quote_status, userDetails,
        // markupDetails) blank out a value the row already had.
        if (full) setSearchQueryData((prev) => mergeFullQueryDetail(prev, full));
        return full || null;
      })
      .catch((err) => {
        console.error(err);
        return null;
      });
  };

  const handleSeachFormdata = (data) => {

    // Clear any previously-loaded manual-quote rates immediately so switching
    // to a different query can never render the prior query's rates while
    // (or if) the new fetch below fails, errors, or comes back empty — see
    // getManualquoteQueryDetail, which only sets this on a successful,
    // non-empty response.
    setManualQuoteQueryData(null);

    let quteStatus = data?.Quote_status;
    if (USER_ROLES?.includes(userroles)) {
      // "Auto Quoted" now opens the read-only query form (same as "Quote
      // Pending") instead of running a rate search — only "Manual Quoted"
      // jumps straight to the available-rates view for customers/customer-admins.
      quteStatus =
        quteStatus === "Manual Quoted"
          ? "Quote Available"
          : quteStatus;
    } else if (data?.cargoType === "Ocean Cargo" && quteStatus === "Manual Quoted") {
      // Ocean has no air-style inline "Manual Quoted" preview (that path below
      // is air-only — getManualquoteQueryDetail/NewRateAvailableList aren't
      // ocean-aware) — treat it the same as "Quote Available" instead, same
      // as the KAM path above.
      quteStatus = "Quote Available";
    }
    setPageType(quteStatus);
    setSearchQueryData(data);
    setIsEditMode(false);
    dispatch(newqueries(data));
    // Backfill the fields the summary row doesn't carry — see
    // backfillFullQuery. Formik's `enableReinitialize` picks up the patched
    // searchQueryData automatically once the fetch resolves. The promise is
    // kept because the air rate-search branch below needs the full document
    // itself, not just the form patch.
    const fullQueryPromise = backfillFullQuery(data);
    // console.log(data, "data", quteStatus,"dataid",data?._id);
    if (quteStatus === "Quote Available") {
      // Every branch below leaves this page for a results page. Flag it so the
      // render short-circuits to a loader instead of painting the query-detail /
      // search form for the moment it takes to resolve the query and navigate.
      setOpeningRates(true);
      if (USER_ROLES?.includes(userroles)) {
        if (data.cargoType === "Ocean Cargo") {
          // Ocean queries have their own results page/redux slice
          // (oceanRates) — mirrors ShipmentRateForm's findflights(), which is
          // the already-working KAM edit-and-search path for this same data.
          const openOceanRates = () => {
            setReceivingResponse(null);
            dispatch(updatequeryid(data?._id));
            // Manual-quote rows: same `manualQuoteOnly` flag the air branch below
            // puts on /ratesearch/all — the rates were quoted by hand, so the
            // backend needn't re-run the Freightify fan-out to answer.
            const oceanSearchquery = buildOceanSearchQuery(data, {
              manualQuoteOnly: data?.Quote_status === "Manual Quoted",
            });
            dispatch(searchOceanRate(oceanSearchquery));
            // Manual-quote rows: flag the results URL with ?manualQuote=true, the
            // same marker the air branch below uses. Ocean's results page
            // (OceanMainRateCom) reads it purely for navigation — its "Edit
            // Query" back button returns to this Queries list instead of the
            // standalone /search-rates form, mirroring the air results page.
            navigate(
              data?.Quote_status === "Manual Quoted"
                ? "/search-rates/ocean-rates-result?manualQuote=true"
                : "/search-rates/ocean-rates-result",
              { state: buildOceanResultsState(oceanSearchquery) }
            );
          };
          // Unverified-user search gate (utils/queryLimit), shared with air —
          // air gates on its results page, ocean has to gate before the search
          // fires. Blocked: stay on the list with the verify-email modal; the
          // search runs once the email is verified.
          isQueryLimitReached(currentUser).then((blocked) => {
            if (!blocked) return openOceanRates();
            pendingOceanOpenRef.current = openOceanRates;
            setOpeningRates(false);
            setPageType("table");
            setShowVerifyModal(true);
          });
          return;
        }
        // Air: this payload is not just prefill — Newratelisting posts it
        // straight to /ratesearch/all, and airCargoValidator rejects the
        // request unless it carries the origin/destination airport detail
        // (city + country name, port marker lat/lng, pickup flags and
        // distances), goodsDetails (cargo ready date, commodityId, special
        // handling codes) and the full packagingDetails (packagingDetailsBy,
        // shipmentTotal.totalvolume, packagingType.packages[]). The summary
        // row carries none of that, so build off the FULL query document from
        // backfillFullQuery's already in-flight fetch — merged over the row so
        // the keys only the row has (Quote_status, which decides
        // isManualQuoted) survive.
        //
        // A failed fetch falls back to the row: the search may then be
        // rejected, but that is strictly better than navigating nowhere, and
        // it is the same payload this path sent before.
        setReceivingResponse(null);
        dispatch(updatequeryid(data?._id));
        fullQueryPromise.then((full) => {
          const queryDoc = full ? mergeFullQueryDetail(data, full) : data;
          const formData = buildAirSearchPayload(queryDoc);
          // Republish the merged document: Newratelisting reads the stored
          // query (`newqueries`) for the commodity/temperature the payload
          // itself doesn't carry, and the row alone has neither.
          dispatch(newqueries(queryDoc));
          dispatch(updateStoredSearch({ nodeData: formData }));
          // Manual-quote rows: flag the results URL with ?manualQuote=true so
          // the results page (Newratelisting) knows to only wire up the MQ_res
          // socket and tell /ratesearch/all not to fan out to all providers.
          navigate(
            formData.isManualQuoted
              ? "/search-rates/search-rates-result?manualQuote=true"
              : "/search-rates/search-rates-result"
          );
        });
        return;
      } else {
        dispatch(updatequeryid(data?._id));
        if (data.cargoType === "Ocean Cargo") {
          // Same direct-to-results path as the KAM branch above — without this,
          // `/search-rates?queryId=` (NewSearchRates) has no way to know this
          // query is Ocean (its state has no `searchBy`/redux `oceanRates`
          // prefill) and silently falls back to showing the Air Cargo tab, so
          // the customer never sees their ocean rates at all.
          setReceivingResponse(null);
          const oceanSearchquery = buildOceanSearchQuery(data);
          dispatch(searchOceanRate(oceanSearchquery));
          navigate("/search-rates/ocean-rates-result", {
            state: buildOceanResultsState(oceanSearchquery),
          });
          return;
        }
        // Air: go straight to the rate-results page. This used to navigate to
        // `/search-rates?queryId=` — the NewSearchRates FORM — which did nothing
        // but prefill itself from the query, auto-submit and forward here, so the
        // whole search-form UI flashed on screen in between. Same payload build
        // as the customer branch above; `openingRates` (set at the top of this
        // block) renders a loader while the full query document is in flight.
        setReceivingResponse(null);
        fullQueryPromise.then((full) => {
          const queryDoc = full ? mergeFullQueryDetail(data, full) : data;
          const formData = buildAirSearchPayload(queryDoc);
          dispatch(newqueries(queryDoc));
          dispatch(updateStoredSearch({ nodeData: formData }));
          navigate(
            formData.isManualQuoted
              ? "/search-rates/search-rates-result?manualQuote=true"
              : "/search-rates/search-rates-result"
          );
        });
        return;
      }
    }
    else if
      (quteStatus === "Manual Quoted") {
      getManualquoteQueryDetail(data?._id);
    }
    if (USER_ROLES?.includes(userroles)) return;
    if (
      data?.Quote_status?.toLowerCase()?.trim().replace(/\s+/g, "") ===
      "autoquoted" ||
      data?.Quote_status?.toLowerCase()?.trim().replace(/\s+/g, "") ===
      "bookingconfirmed"
    ) {
      dispatch(get_booking_markup(data?._id))
        .then((res) => {
          setMarkup_detail(res?.payload?.data);
        })
        .catch((err) => { });
    }
  };

  const semd_manualQuote = () => {
    if (searchQueryData?.cargoType === "Ocean Cargo") {
      dispatch(setOceanQueryDetail(searchQueryData));
      navigate("/send-manual-quote-ocean");
    } else {
      dispatch(setQueryDetail(searchQueryData));
      navigate("/send-manual-quote-v2");
    }
  };

  const [isEditMode, setIsEditMode] = useState(false);

  // "View" (eye) — open the query in a READ-ONLY form: every field disabled and
  // NO search button. Renders the same detail form the Auto-Quoted view uses
  // (type="kam_admin") via a dedicated "view" pageType. The rate search the eye
  // used to run for a "Quote Available" (Manual Quoted) row now lives on the
  // separate magnifying-glass button (handleSeachFormdata).
  const handleViewQuery = (record) => {
    setManualQuoteQueryData(null);
    setSearchQueryData(record);
    dispatch(newqueries(record));
    setIsEditMode(false);
    setPageType("view");
    // Same summary-vs-full-document gap as handleSeachFormdata/handleEditQuery.
    backfillFullQuery(record);
  };

  // KAM/admin-only "View" for a "Quote Available" (Manual Quoted) Air row —
  // opens the submitted manual quotation in the same form used to create it
  // (ManualQuoteHtmlUI, at /send-manual-quote-v2), with every field disabled
  // via the redux `viewOnly` flag. GET /manual-quote/get-manual-quote-detail/:queryId
  // takes the query's own _id directly and returns the full quote doc
  // (airlines[], queryId populated) in one call.
  //
  // This is the ONE pre-booking status that did NOT move to the shared
  // query-detail page (see handleAirQueryView): what a KAM needs from a quoted
  // row is the quotation they sent — the airlines, rates and validity — not a
  // restatement of the query, and only this form carries it.
  const handleViewManualQuote = (record) => {
    dispatch(getManualQuoteDetailById(record?._id)).then((res) => {
      const nodeData = res?.payload?.nodeData;
      if (!nodeData) return;
      dispatch(
        setManualQuoteDetail({
          manualQuoteDetails: nodeData,
          queryDetails: nodeData?.queryId,
          viewOnly: true,
        })
      );
      navigate("/send-manual-quote-v2");
    });
  };

  // KAM/admin-only "Edit" for a "Quote Available" (Manual Quoted) Air row —
  // same fetch/form as handleViewManualQuote, but viewOnly: false so
  // ManualQuoteHtmlUI renders editable and submits via editManualQuote
  // (PUT /manual-quote/:manualqueryId) to update the existing quote.
  const handleEditManualQuote = (record) => {
    dispatch(getManualQuoteDetailById(record?._id)).then((res) => {
      const nodeData = res?.payload?.nodeData;
      if (!nodeData) return;
      dispatch(
        setManualQuoteDetail({
          manualQuoteDetails: nodeData,
          queryDetails: nodeData?.queryId,
          viewOnly: false,
        })
      );
      navigate("/send-manual-quote-v2");
    });
  };

  // KAM/admin-only "View" for a "Quote Available" (Manual Quoted) Ocean row —
  // mirrors handleViewManualQuote (air): opens the submitted quote in the
  // same form used to create it (OceanManualQuote, at
  // /send-manual-quote-ocean), with every field disabled via the redux
  // `viewOnly` flag. GET /ocean-manual-quote/get-manual-quote-detail/:queryId
  // takes the query's own _id directly and returns the full quote doc.
  const handleViewOceanManualQuote = (record) => {
    dispatch(getOceanManualQuoteDetailById(record?._id)).then((res) => {
      const nodeData = res?.payload?.nodeData;
      if (!nodeData) return;
      dispatch(
        setOceanManualQuoteDetail({
          manualQuoteDetails: nodeData,
          queryDetails: nodeData?.queryId,
          viewOnly: true,
        })
      );
      navigate("/send-manual-quote-ocean");
    });
  };

  // KAM/admin-only "Edit" for a "Quote Available" (Manual Quoted) Ocean row —
  // same fetch/form as handleViewOceanManualQuote, but viewOnly: false so
  // OceanManualQuote renders editable and submits via editOceanManualQuote
  // (PUT /ocean-manual-quote/:manualqueryId) to update the existing quote.
  const handleEditOceanManualQuote = (record) => {
    dispatch(getOceanManualQuoteDetailById(record?._id)).then((res) => {
      const nodeData = res?.payload?.nodeData;
      if (!nodeData) return;
      dispatch(
        setOceanManualQuoteDetail({
          manualQuoteDetails: nodeData,
          queryDetails: nodeData?.queryId,
          viewOnly: false,
        })
      );
      navigate("/send-manual-quote-ocean");
    });
  };

  // A booked ocean row — Booking Confirmed, Booking Updated, Confirmation
  // Awaited or Booking Cancelled, for EVERY role — opens the read-only
  // "Booking Details" page (OceanCargoDetails) instead of the query/search form.
  // OceanCargoDetails reads its queryId from the URL's ?queryId= (with a redux
  // fallback), so pass both — exactly like the air mirror
  // (handleAirBookingView) and OceanCargoList.jsx.
  // `from=queries` matches the air mirror: the detail page sits under
  // /booking-history/*, so without it the top nav lights up "Bookings" even
  // though the user is still inside the Queries section (see FROM_MENU_PATHS in
  // layouts/Header.jsx).
  // `cargoTab=2` rides along so the page's back button can return to the OCEAN
  // tab. This page seeds `cargoTab` from the URL on mount and defaults to Air,
  // so a bare /queries put the user back on the Air tab even though the booking
  // they had just opened was an ocean one — see handleBack in
  // components/BookingHistory/OceanCargoDetails.jsx.
  const handleOceanBookingView = (record) => {
    dispatch(savequeryid(record?._id));
    navigate(
      `/booking-history/ocean-cargo-booking-details?queryId=${record?._id}&from=queries&cargoTab=2`
    );
  };

  // Pre-booking sibling of handleOceanBookingView, and the ocean mirror of
  // handleAirQueryView: an Ocean row that has NOT been booked yet — Live Rates
  // Available (Auto Quoted) and Quote Pending — opens the SAME page in query
  // mode (`&view=query`) rather than the rate-results page it used to.
  //
  // The mode flag is not decoration: those rows have no booking, and
  // single_booking_data answers an unbooked queryId with 404, which the page
  // treats as "not yours to see" and bounces on. In query mode it loads the
  // query document instead and hides every booking-only section (carrier,
  // charges, rate, schedule, MBL, payment) — see OceanCargoDetails' header.
  //
  // A Manual Quoted row lands here only once its quote has EXPIRED (the badge
  // reads "Quote Expired"): the quotation can't be acted on any more, so the
  // query on a shareable URL beats a lapsed rate sheet. While the quote is
  // still valid it stays where it was — a KAM/admin needs the quotation they
  // sent, which only the manual-quote form shows
  // (handleViewOceanManualQuote), and a customer keeps the read-only query
  // form that sits next to their "Search Rates" action.
  //
  // The actions that DO something with the query (Search Rates, Edit Query) are
  // untouched and still sit next to the eye.
  const handleOceanQueryView = (record) => {
    dispatch(savequeryid(record?._id));
    navigate(
      `/booking-history/ocean-cargo-booking-details?queryId=${record?._id}&from=queries&cargoTab=2&view=query`
    );
  };

  // A successful save in the documents popup's BL grid hands back the
  // normalised list the backend stored. Stamp it onto that booking's row in
  // `data` so reopening the popup shows the saved rows instead of the list
  // snapshot from the last fetch, then close the popup — the save is the whole
  // point of the popup, so a 200 ends the task.
  //
  // Only called on a 200; a failed save leaves the popup open with the edits
  // intact so they can be retried.
  const handleBlDetailsSaved = (savedBlDetails) => {
    const savedQueryId = oceanDocRecord?._id;
    setData((prev) =>
      Array.isArray(prev)
        ? prev.map((row) =>
            row?._id === savedQueryId ? { ...row, blDetails: savedBlDetails } : row
          )
        : prev
    );
    // Match on id, not just "something is open" — the popup may have been
    // closed and reopened on a different booking while the save was in flight,
    // and that booking's edits shouldn't be dismissed by this response.
    setOceanDocRecord((prev) => (prev?._id === savedQueryId ? null : prev));
  };

  // Air mirror of handleOceanBookingView: a Booking Confirmed, Confirmation
  // Awaited or Booking Cancelled air row opens the read-only air "Booking
  // Details" page (pages/BookingDetailPage.jsx) instead of the query/search
  // form. That page reads its queryId from the URL (with a redux fallback), so
  // pass both — exactly like the customer's own air booking-history list
  // (AirCargoList).
  // `from=queries` tells the detail page's back button to return to the queries
  // list rather than walking the history stack — see handleBack in
  // BookingDetailPage.jsx, which picks /queries or /QueriesKAM from the
  // viewer's role.
  // KAM/admin go through here too, on the same three statuses as the customer
  // (Booking Confirmed, Confirmation Awaited, Booking Cancelled). They get the
  // SAME layout the customer sees on that page; the only KAM-side addition is
  // its "Markup Details" section, which replaced the markup breakup the old
  // inline /QueriesKAM view carried.
  const handleAirBookingView = (record) => {
    dispatch(savequeryid(record?._id));
    navigate(`/booking-history/air-booking-details?queryId=${record?._id}&from=queries`);
  };

  // Pre-booking sibling of handleAirBookingView: an Air row that has NOT been
  // booked yet — Live Rates Available (Auto Quoted), Quote Pending, and (for
  // customers/customer-admins only) Quote Available (Manual Quoted) plus its
  // expired variant — opens the SAME "Booking Details" page in query mode
  // (`&view=query`), instead of the inline query form / rate-results page it
  // used to. BookingDetailPage hides the sections a query can't fill (AWB,
  // carrier, charges, rates, flight legs, markup) rather than showing them empty.
  //
  // The one status NOT routed here is a KAM/admin's own Quote Available row:
  // they need the quotation they sent, which only the manual-quote form shows —
  // see handleViewManualQuote.
  //
  // The row itself rides along in navigation state: it IS the
  // admin/booking-history record, and it carries the status fields
  // (Quote_status, quoteExpiry, userDetails, organizationDetails) that
  // search_cargo_result_detail — the page's own fetch, which covers a refresh
  // or a pasted link — doesn't return. See mergeQueryRecord there.
  //
  // The actions that DO something with the query (Search Rates for customers,
  // Edit Query / Edit Manual Quote for KAM/admin) are untouched and still sit
  // next to the eye.
  const handleAirQueryView = (record) => {
    dispatch(savequeryid(record?._id));
    navigate(
      `/booking-history/air-booking-details?queryId=${record?._id}&from=queries&view=query`,
      { state: { queryRecord: record } }
    );
  };

  const handleEditQuery = (record) => {
    // Customer / customer-admin: "Edit" just deep-links into the search form
    // (which prefills + re-quotes from the queryId) — no inline edit view here.
    if (USER_ROLES?.includes(userroles)) {
      // pagetype=QUERIES marks this as a Queries-page edit so the search form
      // opens with every field editable (see NewsearchForm's isQueriesEdit).
      // For Ocean the ShipmentRateForm can't read the URL params itself, so
      // NewSearchRates fetches + prefills from the record's cargoType/queryId;
      // pass `searchBy` so it lands on the Ocean tab without flashing Air first.
      navigate(`/search-rates?queryId=${record?._id}&pagetype=QUERIES`, {
        state:
          record?.cargoType === "Ocean Cargo"
            ? { ...record, searchBy: "Ocean Cargo" }
            : record,
      });
      return;
    }
    if (record?.Quote_status === "Confirmation Awaited" && !USER_ROLES?.includes(userroles)) {
      if (record?.cargoType === "Ocean Cargo") {
        // OceanCargoDetails reads its queryId from the URL's ?queryId= (with a
        // redux fallback), so pass both — same as OceanCargoList.jsx and the
        // air /booking-view path below. `from=queries` keeps the top nav on
        // "Queries" and `cargoTab=2` returns the back button to the Ocean tab —
        // see handleOceanBookingView.
        // `assign=1` is what separates this button from the eye, which now
        // opens the same URL: it tells the page to jump straight into the
        // "assign MBL & confirm" flow, exactly as this button always has. The
        // eye omits it and gets the read-only page (whose "Assign Tracking
        // Number" banner is still there if the KAM wants the flow from there).
        dispatch(savequeryid(record?._id));
        navigate(
          `/booking-history/ocean-cargo-booking-details?queryId=${record?._id}&from=queries&cargoTab=2&assign=1`
        );
      } else {
        navigate(`/booking-view?queryId=${record?._id}`);
      }
      return;
    }
    const newPageType =
      record?.Quote_status === "Auto Quoted" ? "Auto Quoted Edit" : "Quote Pending";
    setPageType(newPageType);
    // Stamp queryId onto the record (it only carries `_id` as fetched) so
    // ShipmentRateForm's onSubmit (editQuery?.queryId branch) sends
    // queryId + requestType:2 when the KAM re-searches from this edit
    // screen — re-quoting THIS query instead of spawning a new one.
    const editableSearchQueryData =
      record?.cargoType === "Ocean Cargo"
        ? { ...record, queryId: record?._id }
        : record;
    setSearchQueryData(editableSearchQueryData);
    // See handleSeachFormdata: this page renders manualQuoteQueryData
    // whenever it's non-null, regardless of pageType, so it must be cleared
    // here too or a previously-viewed query's rates could still show through.
    setManualQuoteQueryData(null);
    dispatch(newqueries(editableSearchQueryData));
    setIsEditMode(true);
    // Same summary-vs-full-document gap as handleSeachFormdata: backfill
    // every field only the full query document carries so the edit form shows
    // everything the customer actually entered. Air goes out to
    // ratesearch/get-query here — the KAM edit screen used to render straight
    // off the booking-history row, which is why it was missing whatever the
    // list projection drops. See backfillFullQuery.
    backfillFullQuery(editableSearchQueryData);
  };

  // KAM-only "Create Job" for a Booking Confirmed air query whose customer is already on Hike
  // (is_hike_user_created === 1) but has no Hike job yet. Successful rows are
  // tracked locally (query _id -> hike_job_id) so the button disappears and the
  // Hike Job ID column fills in without refetching the list.
  const isKam = userroles === "kam";
  const [createdHikeJobs, setCreatedHikeJobs] = useState({});
  const [creatingHikeJobId, setCreatingHikeJobId] = useState(null);

  const getHikeJobId = (record) =>
    createdHikeJobs[record?._id] || record?.hike_job_id || null;

  const canCreateHikeJob = (record) =>
    isKam &&
    record?.Quote_status === "Booking Confirmed" &&
    Number(record?.is_hike_user_created) === 1 &&
    // Only when the API explicitly says no job yet (is_hike_job_created === 0);
    // a missing flag hides it. Also hidden if a job id exists or one was just
    // created from this page.
    record?.is_hike_job_created != null &&
    Number(record.is_hike_job_created) === 0 &&
    !record?.hike_job_id &&
    !(record?._id in createdHikeJobs);

  const handleCreateHikeJob = (record) => {
    const queryId = record?._id;
    if (!queryId || creatingHikeJobId) return;
    setCreatingHikeJobId(queryId);
    // Hide the icon as soon as it's clicked so it can't be pressed twice while
    // the (slow) Hike call is in flight.
    setCreatedHikeJobs((prev) => ({ ...prev, [queryId]: "" }));
    dispatch(create_hike_job({ queryId }))
      .then((res) => {
        const status = res?.payload?.status;
        const resData = res?.payload?.data;
        if (status >= 200 && resData?.success !== false) {
          const jobId = resData?.hike_job_id ?? resData?.data?.hike_job_id ?? "";
          setCreatedHikeJobs((prev) => ({ ...prev, [queryId]: jobId }));
          message.success(resData?.message || "Job created");
        } else {
          // Let the icon come back; the refetch below still hides it if the
          // backend did create the job despite the error response.
          setCreatedHikeJobs(({ [queryId]: _, ...rest }) => rest);
          message.error(resData?.message || "Failed to create job");
        }
      })
      .catch(() => {
        setCreatedHikeJobs(({ [queryId]: _, ...rest }) => rest);
        message.error("Failed to create job");
      })
      .finally(() => {
        setCreatingHikeJobId(null);
        // Always refetch so the row reflects the server's is_hike_job_created /
        // hike_job_id, whatever the response looked like.
        handleSearch();
      });
  };

  const handleCancelQuery = (record) => {
    console.log("Cancel query", record?._id);
  };

  return (
    <div className="queryList">
      <MainContainer>
        {/* { pageType === "table" && manualQuoteQueryData === null && ( */}
        {/* userroles === "customer admin ,customer" */}
        {
          (pageType === "table" || (!USER_ROLES?.includes(userroles) || manualQuoteQueryData === null)) && (
            // <div>
            <CustomeHeader
              role_type={USER_ROLES?.includes(userroles)}
              pageType={pageType}
              // image={
              //   <IconImg
              //     onClick={() => handleBack()}
              //     src={require("../../../src/assets/admin/images/back_arrow.png")}
              //   />
              // }
              title="Queries"
              description="View all queries / request from user assigned."
            />
            // </div>
          )
        }

        {/* )} */}


        {/* `openingRates` holds the list on screen while a "Quote Available" row is
            being opened, so the query-detail form never paints on the way out to
            the results page — the table just shows its own loading overlay. */}
        {pageType === "table" || openingRates ? (
          <div className="bq-wrap-query">
          <ContentContainer>
            <Tabs
              activeKey={cargoTab}
              type="card"
              tabBarGutter={5}
              onChange={(key) => {
                setCargoTab(key);
                setCurrentPage(1);
                // Without this, switching tabs after having paginated on the
                // previous tab left a stale `page` in `state`, so the next
                // fetch silently asked the backend for that old page while
                // the UI showed page 1 — rows and pagination footer disagreed.
                setState((prev) => ({ ...prev, page: 1, limit: pageSize }));
              }}
              items={[
                { key: 1, label: "Air Cargo" },
                { key: 2, label: "Ocean Cargo" },
              ]}
            />
            <div className="bq-wrap">
              {/* ── Filter Panel ── */}
              <div className="bq-filter-panel">
                <div
                  className="bq-filter-header"
                  onClick={() => setFilterOpen((o) => !o)}
                >
                  <span className="bq-filter-title">Filters &amp; Search</span>
                  <svg
                    className={`bq-filter-chevron${filterOpen ? " open" : ""}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                <div className={`bq-filter-body${filterOpen ? " open" : ""}`}>
                  <div className="bq-filter-controls">
                    <div className="bq-field">
                      <label>Search</label>
                      <input
                        type="text"
                        placeholder="Search queries…"
                        onChange={(e) => onSearch(e.target.value)}
                      />
                    </div>
                    <div className="bq-field">
                      <label>Booking Status</label>
                      <select
                        value={state.status || ""}
                        onChange={(e) =>
                          setState((prev) => ({ ...prev, status: e.target.value }))
                        }
                      >
                        <option value="">All</option>
                        <option value="2">Auto Quoted</option>
                        <option value="3">Quote Pending</option>
                        <option value="5">Manual Quoted</option>
                        <option value="4">Booking Confirmed</option>
                        <option value="6">Confirmation Awaited</option>
                      </select>
                    </div>

                    <div className="bq-field">
                      <label>Origin</label>
                      <input
                        type="text"
                        placeholder="e.g., SOHAR"
                        value={state.origin || ""}
                        onChange={(e) =>
                          setState((prev) => ({ ...prev, origin: e.target.value }))
                        }
                      />
                    </div>
                    <div className="bq-field">
                      <label>Destination</label>
                      <input
                        type="text"
                        placeholder="e.g., HAMBURG"
                        value={state.destination || ""}
                        onChange={(e) =>
                          setState((prev) => ({ ...prev, destination: e.target.value }))
                        }
                      />
                    </div>
                    {cargoTab === 2 && (
                      <div className="bq-field">
                        <label>FCL / LCL</label>
                        <select
                          value={state.mode || ""}
                          onChange={(e) =>
                            setState((prev) => ({ ...prev, mode: e.target.value }))
                          }
                        >
                          <option value="">All</option>
                          <option value="FCL">FCL</option>
                          <option value="LCL">LCL</option>
                        </select>
                      </div>
                    )}
                    <div className="bq-field">
                      <label>Carrier</label>
                      <input
                        type="text"
                        placeholder="e.g., HMM"
                        value={state.carrier || ""}
                        onChange={(e) =>
                          setState((prev) => ({ ...prev, carrier: e.target.value }))
                        }
                      />
                    </div>
                    <div className="bq-field">
                      <label>Cargo Type</label>
                      <select
                        value={state.cargoCategory || ""}
                        onChange={(e) =>
                          setState((prev) => ({ ...prev, cargoCategory: e.target.value }))
                        }
                      >
                        <option value="">All</option>
                        <option value="FAK">FAK</option>
                        <option value="HAZARDOUS">Hazardous</option>
                      </select>
                    </div>

                    <div className="bq-field">
                      <label>Departure Date</label>
                      <DatePicker
                        format="DD-MM-YYYY"
                        value={state.departure_date ? dayjs(state.departure_date, "YYYY-MM-DD") : null}
                        onChange={(_, dateStr) =>
                          setState((prev) => ({ ...prev, departure_date: dateStr ? dayjs(dateStr, "DD-MM-YYYY").format("YYYY-MM-DD") : "" }))
                        }
                        placeholder="DD-MM-YYYY"
                        style={{ width: "100%" }}
                        allowClear
                      />
                    </div>
                    <div className="bq-field">
                      <label>Booking Date</label>
                      <DatePicker
                        format="DD-MM-YYYY"
                        value={state.booking_date ? dayjs(state.booking_date, "YYYY-MM-DD") : null}
                        onChange={(_, dateStr) =>
                          setState((prev) => ({ ...prev, booking_date: dateStr ? dayjs(dateStr, "DD-MM-YYYY").format("YYYY-MM-DD") : "" }))
                        }
                        placeholder="DD-MM-YYYY"
                        style={{ width: "100%" }}
                        allowClear
                      />
                    </div>
                    <div className="bq-field" style={{ justifyContent: "flex-end" }}>
                      <button
                        className="bq-clear-btn"
                        onClick={() => {
                          // Same page/state drift as the tab switch above:
                          // reset the displayed page too, or the pagination
                          // footer keeps showing whatever page was active
                          // before the filters were cleared.
                          setCurrentPage(1);
                          setState({});
                          get_booking_history_air({ cargoType: getCargoTypeParam() });
                        }}
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Table ── */}
              <div className="bq-table-wrap">
                {(loading || openingRates) && (
                  <div className="bq-loading-overlay">
                    <div className="bq-loading-spinner" />
                  </div>
                )}
                <div className="tableResponsiveASAP">
                  
                  <table className={`bq-table${cargoTab === 2 ? " bq-ocean" : ""}`}>
                    <thead>
                      <tr>
                        {/* The table is `table-layout: fixed` (Queries.css), so
                            these widths are honoured as RATIOS: every column is
                            given one, and whatever the table is wider than
                            their sum gets shared out in proportion. That's
                            deliberate — leaving a column unsized instead makes
                            it swallow the entire surplus and opens one big gap
                            beside it, and dropping `fixed` puts the surplus
                            back into the narrow columns (#, Name, Mode). */}
                        <th style={{ width: 34 }}>#</th>
                        {!USER_ROLES.includes(userroles) && (
                          <th style={{ width: 85 }}>Name</th>
                        )}
                        {cargoTab === 2 ? (


                          <>
                            {/* Mirrors the air tab's AWB Number column: the
                                booking's tracking number leads the tab-specific
                                columns, right after # (and Name where shown). */}
                            <th style={{ width: 105 }}>MBL Number</th>
                            <th style={{ width: 62 }}>Mode</th>
                            <th style={{ width: 70 }}>Origin</th>
                            <th style={{ width: 100 }}>Destination</th>
                            <th style={{ width: 90 }}>Incoterms</th>
                            <th style={{ width: 120 }}>Load Type</th>
                            <th style={{ width: 95 }}>Containers</th>
                            <th style={{ width: 170 }}>Container Details</th>
                            <th style={{ width: 105 }}>Weight (KG)</th>
                            <th style={{ width: 140 }}>Created On</th>
                          </>
                        ) : (
                          <>
                            <th style={{ width: 105 }}>AWB Number</th>
                            {isKam && <th style={{ width: 110 }}>Hike Job ID</th>}
                            <th style={{ width: 80 }}>Origin</th>
                            <th style={{ width: 105 }}>Destination</th>
                            <th style={{ width: 340 }}>Shipment Details</th>
                            <th style={{ width: 140 }}>Departure Date</th>
                            <th style={{ width: 135 }}>Booking Date</th>
                          </>
                        )}
                        <th className="bq-col-status" style={{ width: 155 }}>Status</th>
                        <th className="bq-col-action" style={{ width: 120, textAlign: "center" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // `data` is already scoped to the active tab by the server
                        // (the `cargoType` param on every fetch) — don't re-filter it
                        // client-side. A per-record heuristic here previously dropped
                        // legitimate rows whenever a record's `cargoType` field was
                        // missing, silently hiding queries from the KAM's list.
                        const cargoFilteredData = data || [];
                        if (!loading && cargoFilteredData.length === 0) {
                          return (
                            <tr>
                              <td
                                colSpan={
                                  cargoTab === 2
                                    ? USER_ROLES.includes(userroles) ? 13 : 14
                                    : USER_ROLES.includes(userroles) ? 9 : isKam ? 11 : 10
                                }
                                className="bq-empty"
                              >
                                No {cargoTab === 2 ? "ocean" : "air"} queries found.
                              </td>
                            </tr>
                          );
                        }
                        return cargoFilteredData.map((record, index) => {
                          const isOcean = cargoTab === 2;
                          const origCode = isOcean
                            ? extractPortCode(record?.originPort?.originPort)
                            : record?.originPort?.originAirport?.match(/\(([^)]+)\)/)?.[1] ?? "—";
                          const destCode = isOcean
                            ? extractPortCode(record?.destinationPort?.destinationPort)
                            : record?.destinationPort?.destinationAirport?.match(/\(([^)]+)\)/)?.[1] ?? "—";
                          const rawStatus = record?.Quote_status;
                          // Show raw Quote_status directly, except "Manual Quoted"
                          // which is surfaced to everyone as "Quote Available", and
                          // "Auto Quoted" which is displayed as "Live Rates Available"
                          // (UI label only — the raw status value is unchanged).
                          const shownStatus =
                            rawStatus === "Manual Quoted"
                              ? "Quote Available"
                              : rawStatus === "Auto Quoted"
                                ? "Live Rates Available"
                                : rawStatus;
                          const displayStatus = USER_ROLES.includes(userroles)
                            ? rawStatus === "Manual Quoted"
                              ? "Quote Available"
                              : rawStatus
                            : rawStatus;
                          const badgeClass =
                            rawStatus === "Manual Quoted"
                              ? "bq-available"
                              : rawStatus === "Auto Quoted"
                                ? "bq-quoted"
                                : rawStatus === "Quote Pending"
                                  ? "bq-pending"
                                  : rawStatus === "Booking Confirmed"
                                    ? "bq-confirmed"
                                    : rawStatus === "Confirmation Awaited"
                                      ? "bq-awaited"
                                      : rawStatus === "No Rates"
                                        ? "bq-norates"
                                        : "bq-quoted";

                          // Manual quotes carry an expiry (UTC ISO). When that
                          // instant is already in the past — compared in IST via
                          // moment-timezone — surface the row as "Quote Expired"
                          // instead of "Quote Available". Everything else stays the
                          // same.
                          //
                          // Two expiry fields exist on a booking-history row and only
                          // the manual quote's own `quoteExpiry` is authoritative;
                          // `queryExpiryDate` is the query's own shelf life and is the
                          // fallback for rows quoted before quoteExpiry was stamped.
                          // Mongo OMITS a key whose source path doesn't exist rather
                          // than nulling it, so both are simply absent on auto-quoted
                          // rows — `??` covers undefined and null alike. (There is no
                          // nodeData.result.queryExpiryDate on this endpoint: that's
                          // the envelope other endpoints wrap their payload in, never
                          // a stored field, so it was dropped from this chain.)
                          const quoteExpiryRaw =
                            record?.quoteExpiry ?? record?.queryExpiryDate ?? null;
                          // `isManualQuoted` is the explicit flag the list now sends;
                          // the status/queryQuotingStatus checks stay as fallbacks for
                          // rows that predate it.
                          const isManualQuote =
                            record?.isManualQuoted === true ||
                            rawStatus === "Manual Quoted" ||
                            shownStatus === "Quote Available" ||
                            record?.queryQuotingStatus === "Manual";
                          // Guard on isValid(): an unparseable expiry must not be read
                          // as "expired" (moment would just return false here, but an
                          // Invalid Date would also reach the tooltip below).
                          const quoteExpiryMoment =
                            quoteExpiryRaw && moment.tz(quoteExpiryRaw, "Asia/Kolkata").isValid()
                              ? moment.tz(quoteExpiryRaw, "Asia/Kolkata")
                              : null;
                          const isQuoteExpired =
                            isManualQuote &&
                            !!quoteExpiryMoment &&
                            quoteExpiryMoment.isBefore(moment.tz("Asia/Kolkata"));
                          // Once the quote has been paid against it's been acted on, so
                          // an elapsed expiry shouldn't mask the real status badge.
                          // Rows still awaiting payment are filtered out of this list
                          // server-side (except ocean booking intents, which are
                          // "Payment Pending" by design), so `payment_status` is
                          // either absent (never acted on) or a booked/paid state —
                          // the mere presence of the key is enough to say the quote
                          // was acted on.
                          const hasPaymentRecord = !!record?.payment_status;
                          const showExpiredStatus = isQuoteExpired && !hasPaymentRecord;
                          const finalShownStatus = showExpiredStatus
                            ? "Quote Expired"
                            : shownStatus;
                          const finalBadgeClass = showExpiredStatus
                            ? "bq-expired"
                            : badgeClass;

                          // Ocean tab uses a simplified column set (Mode / Incoterms /
                          // Load Type / Container Details / Created On) instead of the
                          // Air-specific AWB/shipment/departure/booking-date columns
                          // below. Status and Actions stay frozen (sticky) on the right.
                          //
                          // Field names below match the current
                          // admin/booking-history?cargoType=Ocean+Cargo response (not
                          // the raw Mongo doc, which has a different shape):
                          //   mode                 "FCL" | "LCL"
                          //   containerLoadType    "20 GP, 40 HC" — "" when no lines
                          //   containerDetails.containerDetails[]  { type, quantity, kgs }
                          //   containerDetails.packagingDetails.commodity
                          //   containerDetails.packagingDetails.packagingDatas[]
                          //                        { noofPackages, weight }
                          // Both arrays default to [] server-side rather than going
                          // missing; only `commodity` is omitted when unset. Every
                          // numeric field arrives as a STRING ("2", "18000"), hence the
                          // Number() coercion on each one. The Array.isArray branch
                          // below keeps the older flat-array shape working for any
                          // record still served that way.
                          const containerRows = Array.isArray(record?.containerDetails)
                            ? record.containerDetails
                            : Array.isArray(record?.containerDetails?.containerDetails)
                              ? record.containerDetails.containerDetails
                              : [];
                          const lclPackages = Array.isArray(
                            record?.containerDetails?.packagingDetails?.packagingDatas
                          )
                            ? record.containerDetails.packagingDetails.packagingDatas
                            : [];
                          // `mode` drives the FCL/LCL split. If it's ever missing, fall
                          // back to whichever collection actually has rows so the cell
                          // doesn't silently render the empty LCL variant.
                          const modeUpper = (record?.mode || "").toUpperCase();
                          const isFCL =
                            modeUpper === "FCL" ||
                            (!modeUpper && containerRows.length > 0 && lclPackages.length === 0);
                          // Lines come as { type, quantity } from the current search
                          // form, but older queries stored { containerSize,
                          // containerType, containerCount } — read either. Lines of
                          // the same type are merged so the cell reads
                          // "1x 20 GP, 2x 20 DV" rather than repeating a type, and
                          // lines with no type are skipped (no bare "1x").
                          const containerQtyByType = new Map();
                          containerRows.forEach((c) => {
                            const type =
                              c?.type ||
                              [c?.containerSize, c?.containerType].filter(Boolean).join(" ");
                            if (!type) return;
                            const qty = Number(c?.quantity ?? c?.containerCount) || 0;
                            containerQtyByType.set(type, (containerQtyByType.get(type) || 0) + qty);
                          });
                          const containerTypeLabels = [...containerQtyByType].map(
                            ([type, qty]) => (qty ? `${qty}x ${type}` : type)
                          );
                          // `containerLoadType` comes back "" on some rows even though
                          // the container lines are present, so fall back to the
                          // distinct line types in entry order ("20 GP, 40 HC").
                          const loadTypes = record?.containerLoadType
                            ? record.containerLoadType.split(",").map((t) => t.trim()).filter(Boolean)
                            : [...containerQtyByType.keys()];
                          const containerTotalCount = containerRows.reduce(
                            (sum, c) => sum + (Number(c?.quantity ?? c?.containerCount) || 0),
                            0
                          );
                          const containerTotalKgs = totalContainerWeight(containerRows);
                          const lclTotalPackages = lclPackages.reduce(
                            (sum, p) => sum + (Number(p?.noofPackages) || 0),
                            0
                          );
                          const lclTotalWeight = lclPackages.reduce(
                            (sum, p) => sum + (Number(p?.weight) || 0),
                            0
                          );
                          const lclCommodity =
                            record?.containerDetails?.packagingDetails?.commodity || "—";

                          if (cargoTab === 2) {
                            return (
                              <tr key={record._id || index}>
                                <td>{index + 1 + (currentPage - 1) * pageSize}</td>
                                {!USER_ROLES.includes(userroles) && (
                                  <td>
                                    <div className="bq-name-cell">
                                      <div>
                                        <div className="bq-name-primary">
                                          {record?.userDetails?.full_name ?? "—"}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                )}
                                {/* Assigned by the KAM at the "assign MBL &
                                    confirm" step, so it's absent on every row
                                    that isn't a confirmed booking yet. Older/
                                    manual-quote bookings expose it as
                                    `airWaybillNumber`, same fallback as the
                                    ocean detail view. */}
                                <td>{record?.mblNumber || record?.airWaybillNumber || "—"}</td>
                                <td>{record?.mode || "—"}</td>
                                <td>{origCode ?? "—"}</td>
                                <td>{destCode ?? "—"}</td>
                                <td>{record?.paymentTerms?.incoTerms ?? "—"}</td>
                                <td>
                                  <CollapsedList items={loadTypes} />
                                </td>
                                {/* LCL rows show their package count / commodity here. */}
                                <td>
                                  {isFCL ? containerTotalCount || "—" : lclTotalPackages || "—"}
                                </td>
                                <td>
                                  {isFCL ? <CollapsedList items={containerTypeLabels} /> : lclCommodity || "—"}
                                </td>
                                <td>
                                  {isFCL
                                    ? containerTotalKgs
                                      ? containerTotalKgs.toFixed(2)
                                      : "—"
                                    : lclTotalWeight
                                      ? lclTotalWeight.toFixed(2)
                                      : "—"}
                                </td>
                                <td>
                                  {record?.createdAt
                                    ? moment(record.createdAt)
                                        .utcOffset("+05:30")
                                        .format("DD MMM YYYY, h:mm A")
                                    : "—"}
                                </td>
                                <td className="bq-col-status">
                                  {/* Expiry hover only makes sense while the manual
                                      quote is still awaiting action — once it's
                                      booked/cancelled/expired the badge already says
                                      so, so skip the tooltip there. */}
                                  {finalShownStatus === "Quote Available" && quoteExpiryMoment ? (
                                    <Tooltip
                                      title={`Expires: ${quoteExpiryMoment.format(
                                        "DD MMM YYYY, h:mm A"
                                      )} IST`}
                                    >
                                      <span
                                        className={`bq-badge ${finalBadgeClass}`}
                                        style={{ cursor: "pointer" }}
                                      >
                                        {finalShownStatus}
                                      </span>
                                    </Tooltip>
                                  ) : (
                                    <span className={`bq-badge ${finalBadgeClass}`}>
                                      {finalShownStatus}
                                    </span>
                                  )}
                                </td>
                                <td className="bq-col-action">
                                  <div className="bq-actions">
                                    {/* View — read-only form (all fields disabled, no
                                        search button). For a "Quote Available" (Manual
                                        Quoted) row the eye no longer runs the rate
                                        search — that moved to the search button below. */}
                                    <button
                                      className="bq-act-btn view"
                                      title="View"
                                      onClick={() => {
                                        // Every booked status opens the read-only ocean
                                        // Booking Details page for EVERY role — exactly
                                        // like the air mirror below. KAM/admin used to fall
                                        // through to handleSeachFormdata, which rendered
                                        // OceanCargoDetails INLINE on this page: no
                                        // shareable URL, no surviving a refresh, and a
                                        // different surface from the one the customer saw.
                                        // Both roles now land on the same standalone page.
                                        // "Confirmation Awaited" is here for every role too
                                        // (it used to be customers-only): the booking exists
                                        // at that point, so the eye should show it. The
                                        // KAM's "Assign MBL" edit button is still the way
                                        // into the editable confirm flow — it passes
                                        // `&assign=1`, which this read-only eye does not.
                                        if (
                                          rawStatus === "Booking Confirmed" ||
                                          rawStatus === "Booking Updated" ||
                                          rawStatus === "Booking Cancelled" ||
                                          rawStatus === "Confirmation Awaited"
                                        ) {
                                          handleOceanBookingView(record);
                                          return;
                                        }
                                        // Unbooked rows open the same page in query mode —
                                        // see handleOceanQueryView.
                                        if (
                                          rawStatus === "Auto Quoted" ||
                                          rawStatus === "Quote Pending"
                                        ) {
                                          handleOceanQueryView(record);
                                          return;
                                        }
                                        if (rawStatus === "Manual Quoted") {
                                          // An EXPIRED manual quote goes to the
                                          // query-mode detail page for every role,
                                          // like the unbooked statuses above. The
                                          // quotation itself is no longer actionable
                                          // — there is nothing left to accept — so
                                          // what's worth showing is the query, on a
                                          // shareable URL, rather than a lapsed
                                          // rate sheet (KAM) or an inline form that
                                          // never leaves /queries (customer).
                                          // Keyed off showExpiredStatus, not
                                          // isQuoteExpired, so it matches exactly the
                                          // rows whose badge reads "Quote Expired":
                                          // once payment is confirmed the quote has
                                          // been acted on and keeps its real status.
                                          if (showExpiredStatus) {
                                            handleOceanQueryView(record);
                                            return;
                                          }
                                          // Still-valid "Quote Available" is unchanged:
                                          // KAM/admin get the quotation they sent,
                                          // customers keep the read-only query-form
                                          // view next to their "Search Rates" action.
                                          !USER_ROLES?.includes(userroles)
                                            ? handleViewOceanManualQuote(record)
                                            : handleViewQuery(record);
                                          return;
                                        }
                                        handleSeachFormdata(record);
                                      }}
                                    >
                                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                      </svg>
                                    </button>

                                    {/* Search Rates — runs the rate search (the eye's old
                                        action) for a "Quote Available" (Manual Quoted) row.
                                        Hidden once the quote has expired.
                                        KAM/admin no longer gets this button (they have the
                                        dedicated view/edit manual-quote actions instead). */}
                                    {rawStatus === "Manual Quoted" && USER_ROLES?.includes(userroles) && !isQuoteExpired && (
                                      <button
                                        className="bq-act-btn search"
                                        title="Search Rates"
                                        onClick={() => handleSeachFormdata(record)}
                                      >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <circle cx="11" cy="11" r="8" />
                                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                        </svg>
                                      </button>
                                    )}

                                    {/* Edit — KAM/admin only: reopens the same Ocean
                                        manual-quote form as the eye button, but editable,
                                        so the quote can be updated and resubmitted. */}
                                    {rawStatus === "Manual Quoted" && !USER_ROLES?.includes(userroles) && (
                                      <button
                                        className="bq-act-btn edit"
                                        title="Edit Manual Quote"
                                        onClick={() => handleEditOceanManualQuote(record)}
                                      >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" />
                                        </svg>
                                      </button>
                                    )}

                                    {/* `isManualQuote` (not the raw isManualQuoted flag)
                                        so a row carrying queryQuotingStatus "Manual"
                                        without the newer boolean is excluded too — a
                                        manually quoted query is edited through the
                                        manual-quote form above, not re-searched. */}
                                    {(rawStatus === "Auto Quoted" || rawStatus === "Quote Pending") && !isManualQuote && (
                                      <button
                                        className="bq-act-btn edit"
                                        title="Edit Query"
                                        onClick={() => handleEditQuery(record)}
                                      >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" />
                                        </svg>
                                      </button>
                                    )}

                                    {/* "Assign MBL" is a KAM/admin action — hide the edit
                                        for customers/customer-admins on Confirmation
                                        Awaited (and Booking Confirmed already has no edit). */}
                                    {rawStatus === "Confirmation Awaited" && !USER_ROLES?.includes(userroles) && (
                                      <button
                                        className="bq-act-btn edit"
                                        title="Assign MBL"
                                        onClick={() => handleEditQuery(record)}
                                      >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" />
                                        </svg>
                                      </button>
                                    )}

                                    {/* Assign BL — KAM/admin only, and only once the
                                        ocean booking is confirmed. Opens the Bill of
                                        Lading List popup for this row (see the Modal at
                                        the bottom of this component). */}
                                    {rawStatus === "Booking Confirmed" && !USER_ROLES?.includes(userroles) && (
                                      <button
                                        className="bq-act-btn doc"
                                        title="Assign BL"
                                        onClick={() => setOceanDocRecord(record)}
                                      >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                          <polyline points="14 2 14 8 20 8" />
                                          <line x1="16" y1="13" x2="8" y2="13" />
                                          <line x1="16" y1="17" x2="8" y2="17" />
                                          <polyline points="10 9 9 9 8 9" />
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          }

                          // Prefer the server-computed shipmentTotal; fall back to the
                          // raw packagingType figures for older records that predate it.
                          const st = record?.packagingDetails?.shipmentTotal;
                          const pkts = st?.totalunits ?? record?.packagingDetails?.packagingType?.totalunits;
                          const chWt = st?.chargeableWeight ?? null;
                          const volWt = st?.volumeWeight ?? record?.packagingDetails?.packagingType?.totalvolume;
                          const wt = st?.totalweight ?? record?.packagingDetails?.packagingType?.totalweight;

                          return (
                            <tr key={record._id || index}>
                              <td>
                                {index + 1 + (currentPage - 1) * pageSize}
                              </td>
                              {!USER_ROLES.includes(userroles) && (
                                <td>
                                  <div className="bq-name-cell">
                                    {/* <div className="bq-name-avatar">
                                      <User size={15} color="#92400e" />
                                    </div> */}
                                    {/* Name only: the list response carries
                                        userDetails.full_name (KAM-side only) and
                                        no organizationDetails, so there's no
                                        secondary org line to render. */}
                                    <div>
                                      <div className="bq-name-primary">
                                        {record?.userDetails?.full_name ?? "—"}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              )}
                              <td>
                                {(() => {
                                  // Shown for every status whenever an AWB exists
                                  // (e.g. a cancelled booking keeps its AWB).
                                  // Combine the 3-digit airline prefix with the AWB
                                  // number as "057-59248615"; fall back gracefully
                                  // when either part is missing.
                                  const prefix = record?.airlinePrefix;
                                  const awb = record?.airWaybillNumber;
                                  return awb
                                    ? prefix
                                      ? `${prefix}-${awb}`
                                      : awb
                                    : "—";
                                })()}
                              </td>
                              {isKam && <td>{getHikeJobId(record) || "—"}</td>}
                              <td>{origCode ?? "—"}</td>
                              <td>{destCode ?? "—"}</td>
                              <td className="bq-td-ship">
                                <div className="bq-ship-cell">
                                  <div className="bq-ship-col">
                                    <span className="bq-ship-lbl">PCS</span>
                                    <span className="bq-ship-val">{pkts ?? "—"}</span>
                                  </div>
                                  <div className="bq-ship-col">
                                    <span className="bq-ship-lbl">CH. WT (KG)</span>
                                    <span className="bq-ship-val">{chWt ?? "—"}</span>
                                  </div>
                                  <div className="bq-ship-col">
                                    <span className="bq-ship-lbl">VOL. WT (KG)</span>
                                    <span className="bq-ship-val">{volWt ?? "—"}</span>
                                  </div>
                                  <div className="bq-ship-col">
                                    <span className="bq-ship-lbl">TOTAL WT (KG)</span>
                                    <span className="bq-ship-val">{wt ?? "—"}</span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                {record?.cargoreadydate
                                  ? moment(record.cargoreadydate)
                                      .utcOffset("+05:30")
                                      .format("DD-MM-YYYY")
                                  : "—"}
                              </td>
                              <td>
                                {(() => {
                                  const bookingDate = record?.booking_confirm_date;
                                  const showBooking =
                                    (bookingDate &&
                                      (record?.Quote_status === "Booking Confirmed" ||
                                        record?.Quote_status === "Booking Cancelled")) ||
                                    record?.Quote_status === "Confirmation Awaited";
                                  const dateText =
                                    showBooking && bookingDate
                                      ? moment(bookingDate).format("DD MMM YYYY")
                                      : "—";
                                  // Cancelled bookings carry a deletedAt timestamp
                                  // (UTC) — surface it in IST via a hover tooltip.
                                  const deletedAt = record?.deletedAt;
                                  const deletedAtIST = deletedAt
                                    ? moment(deletedAt)
                                        .utcOffset("+05:30")
                                        .format("DD MMM YYYY, h:mm A")
                                    : null;
                                  // Also surface the last-updated timestamp (UTC) in
                                  // IST. Skip it when a deletedAt exists on the same
                                  // calendar day to avoid a redundant tooltip line.
                                  const updatedDt = record?.updatedDt;
                                  const sameDate =
                                    deletedAt &&
                                    updatedDt &&
                                    moment(deletedAt)
                                      .utcOffset("+05:30")
                                      .isSame(
                                        moment(updatedDt).utcOffset("+05:30"),
                                        "day"
                                      );
                                  const updatedDtIST =
                                    updatedDt && !sameDate
                                      ? moment(updatedDt)
                                          .utcOffset("+05:30")
                                          .format("DD MMM YYYY, h:mm A")
                                      : null;
                                  const hasTooltip = deletedAtIST || updatedDtIST;
                                  const content = (
                                    <span
                                      className={`inline-flex justify-center items-center gap-1${
                                        hasTooltip ? " cursor-pointer" : ""
                                      }`}
                                    >
                                      <span>{dateText}</span>
                                      {hasTooltip && (
                                        <ClockCircleOutlined className="text-gray-500 cursor-pointer" />
                                      )}
                                    </span>
                                  );
                                  return hasTooltip ? (
                                    <Tooltip
                                      title={
                                        <>
                                          {deletedAtIST && (
                                            <div>Cancelled: {deletedAtIST} IST</div>
                                          )}
                                          {updatedDtIST && (
                                            <div>Updated at: {updatedDtIST} IST</div>
                                          )}
                                        </>
                                      }
                                    >
                                      {content}
                                    </Tooltip>
                                  ) : (
                                    content
                                  );
                                })()}
                              </td>
                              <td>
                                {/* Expiry hover only makes sense while the manual
                                    quote is still awaiting action — once it's
                                    booked/cancelled/expired the badge already says
                                    so, so skip the tooltip there. */}
                                {finalShownStatus === "Quote Available" && quoteExpiryMoment ? (
                                  <Tooltip
                                    title={`Expires: ${quoteExpiryMoment.format(
                                      "DD MMM YYYY, h:mm A"
                                    )} IST`}
                                  >
                                    <span
                                      className={`bq-badge ${finalBadgeClass}`}
                                      style={{ cursor: "pointer" }}
                                    >
                                      {finalShownStatus}
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <span className={`bq-badge ${finalBadgeClass}`}>
                                    {finalShownStatus}
                                  </span>
                                )}
                              </td>
                              <td>
                                <div className="bq-actions">
                                  {/* View — read-only form (all fields disabled, no
                                      search button). For a "Quote Available" (Manual
                                      Quoted) row the eye no longer runs the rate
                                      search — that moved to the search button below. */}
                                  <button
                                    className="bq-act-btn view"
                                    title="View"
                                    onClick={() => {
                                      // Booking Confirmed / Booking Cancelled open the
                                      // read-only air Booking Details page instead of the
                                      // form, for EVERY role — these used to fall through
                                      // to handleSeachFormdata, which rendered the same
                                      // detail inline (the "bookingconfirmed" /
                                      // "bookingcancelled" pageType branch below); the
                                      // standalone page is linkable and reads the booking
                                      // from the same single_booking_data call.
                                      // "Confirmation Awaited" is here for EVERY role too:
                                      // a KAM's eye used to open the inline query form +
                                      // markup (the "confirmationawaited" pageType branch
                                      // below), but the booking exists at that point, so
                                      // both roles now land on the same shareable detail
                                      // page — the KAM just also gets its Markup Details
                                      // section. The KAM's "Assign AWB" edit button is
                                      // still the way into the editable view.
                                      if (
                                        rawStatus === "Booking Confirmed" ||
                                        rawStatus === "Booking Cancelled" ||
                                        rawStatus === "Confirmation Awaited"
                                      ) {
                                        handleAirBookingView(record);
                                        return;
                                      }
                                      // A KAM/admin looking at a "Quote Available"
                                      // (Manual Quoted) row wants the quotation
                                      // they sent — airlines, rates, validity —
                                      // so that one case keeps its original
                                      // read-only manual-quote form rather than
                                      // moving to the query-detail page below.
                                      if (
                                        rawStatus === "Manual Quoted" &&
                                        !USER_ROLES?.includes(userroles)
                                      ) {
                                        handleViewManualQuote(record);
                                        return;
                                      }
                                      // Every other pre-booking status opens the
                                      // same read-only detail page in query mode,
                                      // for every role — see handleAirQueryView.
                                      // The eye used to fan out from here into the
                                      // customer's read-only query form and
                                      // handleSeachFormdata's rate-results page;
                                      // the actions that re-quote or edit the
                                      // query are still on their own buttons below.
                                      if (
                                        rawStatus === "Auto Quoted" ||
                                        rawStatus === "Quote Pending" ||
                                        rawStatus === "Manual Quoted"
                                      ) {
                                        handleAirQueryView(record);
                                        return;
                                      }
                                      handleSeachFormdata(record);
                                    }}
                                  >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                      <circle cx="12" cy="12" r="3" />
                                    </svg>
                                  </button>

                                  {/* Search Rates — runs the rate search (the eye's old
                                      action) for a "Quote Available" (Manual Quoted) row.
                                      Hidden once the quote has expired.
                                      KAM/admin no longer gets this button (they have the
                                      dedicated view/edit manual-quote actions instead). */}
                                  {rawStatus === "Manual Quoted" && USER_ROLES?.includes(userroles) && !isQuoteExpired && (
                                    <button
                                      className="bq-act-btn search"
                                      title="Search Rates"
                                      onClick={() => handleSeachFormdata(record)}
                                    >
                                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="8" />
                                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                      </svg>
                                    </button>
                                  )}

                                  {/* Edit — KAM/admin only: reopens the same manual-quote
                                      form as the eye button, but editable, so the quote can
                                      be updated and resubmitted. */}
                                  {rawStatus === "Manual Quoted" && !USER_ROLES?.includes(userroles) && (
                                    <button
                                      className="bq-act-btn edit"
                                      title="Edit Manual Quote"
                                      onClick={() => handleEditManualQuote(record)}
                                    >
                                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" />
                                      </svg>
                                    </button>
                                  )}

                                  {/* Auto Quoted / Quote Pending — Edit only, no cancel */}
                                  {(rawStatus === "Auto Quoted" || rawStatus === "Quote Pending") && (
                                    <button
                                      className="bq-act-btn edit"
                                      title="Edit Query"
                                      onClick={() => handleEditQuery(record)}
                                    >
                                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" />
                                      </svg>
                                    </button>
                                  )}

                                  {/* Confirmation Awaited — "Assign AWB" is a KAM/admin
                                      action; hide the edit for customers/customer-admins
                                      (Booking Confirmed already has no edit). */}
                                  {rawStatus === "Confirmation Awaited" && !USER_ROLES?.includes(userroles) && (
                                    <>
                                      <button
                                        className="bq-act-btn edit"
                                        title="Assign AWB"
                                        onClick={() => handleEditQuery(record)}
                                      >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4z" />
                                        </svg>
                                      </button>
                                      {/* <button
                                        className="bq-act-btn cancel"
                                        title="Cancel"
                                        onClick={() => handleCancelQuery(record)}
                                      >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <line x1="18" y1="6" x2="6" y2="18" />
                                          <line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                      </button> */}
                                    </>
                                  )}

                                  {/* Create Job — KAM only, air only, Booking Confirmed only: customer exists on
                                      Hike but this query has no Hike job yet. */}
                                  {canCreateHikeJob(record) && (
                                    <button
                                      className="bq-act-btn edit"
                                      title="Create Job"
                                      disabled={creatingHikeJobId === record?._id}
                                      onClick={() => handleCreateHikeJob(record)}
                                    >
                                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="4" y="2" width="16" height="20" rx="2" />
                                        <path d="M9 22v-4h6v4" />
                                        <path d="M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01" />
                                      </svg>
                                    </button>
                                  )}

                                  {/* Booking Confirmed — View only, no edit, no cancel */}
                                </div>
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>

                {/* ── Pagination ── */}
                {totalData > 0 && (() => {
                  const pageCount = Math.max(1, Math.ceil(totalData / pageSize));
                  const fromIdx = (currentPage - 1) * pageSize + 1;
                  const toIdx = Math.min(currentPage * pageSize, totalData);
                  const pageWindow = buildPageWindow(currentPage, pageCount);
                  return (
                    <div className="bq-pagination">
                      <span className="bq-page-info">
                        {fromIdx}–{toIdx} of {totalData.toLocaleString()} queries
                      </span>
                      <div className="bq-page-controls">
                        <select
                          className="bq-page-size-select"
                          value={pageSize}
                          onChange={(e) =>
                            handlePageSizeChange(currentPage, Number(e.target.value))
                          }
                        >
                          {[10, 20, 50].map((n) => (
                            <option key={n} value={n}>
                              {n} / page
                            </option>
                          ))}
                        </select>
                        <button
                          className="bq-page-btn"
                          disabled={currentPage <= 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                          >
                            <polyline points="15 18 9 12 15 6" />
                          </svg>
                        </button>
                        {pageWindow.map((p, i) =>
                          p === "…" ? (
                            <span key={`e${i}`} className="bq-page-ellipsis">
                              …
                            </span>
                          ) : (
                            <button
                              key={p}
                              className={`bq-page-num-btn${p === currentPage ? " active" : ""}`}
                              onClick={() => handlePageChange(p)}
                            >
                              {p}
                            </button>
                          )
                        )}
                        <button
                          className="bq-page-btn"
                          disabled={currentPage >= pageCount}
                          onClick={() => handlePageChange(currentPage + 1)}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                          >
                            <polyline points="9 18 15 12 9 6" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </ContentContainer>
          </div>
        ) : (pageType?.toLowerCase()?.trim().replace(/\s+/g, "") ===
          "autoquoted" || pageType === "view") ? (
          <>
            <ContentContainer>
              <SubHeadContainer className="flex-column">
                <LeftHead className="space-btwn">
                  <TitleHead>
                    <IconImg
                      onClick={() => handleBack()}
                      src={require("../../../src/assets/admin/images/back_arrow.png")}
                    />
                    <SubHeadTitle>Query Detail </SubHeadTitle>
                  </TitleHead>
                </LeftHead>
              </SubHeadContainer>
              {searchQueryData?.cargoType === "Ocean Cargo" ? (
                <ShipmentRateForm
                  type="kam_admin"
                  searchBy={searchQueryData?.cargoType}
                  searchFormData={{
                    searchquery: searchQueryData,
                  }}
                />
              ) : (
                <NewsearchForm
                  type="kam_admin"
                  searchBy={searchQueryData?.cargoType || "Air Cargo"}
                  searchFormData={{
                    searchquery: searchQueryData,
                  }}
                />
              )}
            </ContentContainer>
            {!USER_ROLES?.includes(userroles) && (
              <ContentContainer>
                <MarkupBookingBreakup markup_detail={markup_detail} />
              </ContentContainer>
            )}
            {/* KAM/admin view of a Confirmation Awaited query — markupDetails
                already rides along on the booking-history row (searchQueryData),
                so just surface the percentage, no separate fetch needed.
                USER_ROLES here is actually the CUSTOMER role list
                (["customer", "customer_admin"], see line 95), so KAM/admin is
                the `!USER_ROLES.includes(userroles)` case — same check the
                MarkupBookingBreakup block above it uses. */}
            {!USER_ROLES?.includes(userroles) &&
              searchQueryData?.Quote_status === "Confirmation Awaited" &&
              searchQueryData?.markupDetails?.markup != null && (
                <ContentContainer>
                  <div className="flex items-center gap-2 px-1">
                    <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                      Markup
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {searchQueryData.markupDetails.markup}%
                    </span>
                  </div>
                </ContentContainer>
              )}
          </>
        ) : pageType?.toLowerCase()?.trim().replace(/\s+/g, "") ===
          "quotepending" ||
          pageType?.toLowerCase()?.trim().replace(/\s+/g, "") ===
          "manualquoted" ||
          pageType?.toLowerCase()?.trim().replace(/\s+/g, "") ===
          "confirmationawaited" ||
          pageType?.toLowerCase()?.trim().replace(/\s+/g, "") ===
          "norates" ||
          pageType === "Auto Quoted" ||
          pageType === "Auto Quoted Edit" ||
          pageType === "Manual Quoted" ||
          pageType == "Quote Available" ? (
          <>
            {pageType === "Manual Quoted" && USER_ROLES?.includes(userroles) ? (
              <></>
            ) : pageType === "Quote Available" &&
              USER_ROLES?.includes(userroles) ? (
              // <></>
              <ContentContainer>
                <SubHeadContainer className="flex-column">
                  <LeftHead className="space-btwn">
                    <TitleHead>
                      <IconImg
                        onClick={() => handleBack()}
                        src={require("../../../src/assets/admin/images/back_arrow.png")}
                      />
                      <SubHeadTitle>Query Detail3 </SubHeadTitle>
                    </TitleHead>
                    {/* {!USER_ROLES?.includes(userroles) &&
                      pageType?.toLowerCase()?.trim().replace(/\s+/g, "") ===
                      "quotepending"  || params!==null &&( */}
                    {(
                      (!USER_ROLES?.includes(userroles) &&
                        pageType?.toLowerCase()?.trim().replace(/\s+/g, "") === "quotepending") ||
                      params !== null
                    ) && (<div className="sm:col-span-12 md:col-span-6 lg:col-span-6 col-span-2 relative flex items-end">
                      <button
                        onClick={() => semd_manualQuote()}
                        type="submit"
                        className="text-white bg-primary hover:bg-secondary font-medium rounded-lg text-sm px-4 py-3 text-center  w-full flex items-center justify-center gap-2 shadow-searchbox"
                      >
                        <span>Send Manual Quote</span>
                      </button>
                    </div>
                      )}
                  </LeftHead>
                </SubHeadContainer>
                <FlightRate
                  // type="kam_admin"
                  type="user_customer"

                  searchBy={searchQueryData?.cargoType}
                  searchFormData={{
                    searchquery: searchQueryData?.search_query_data,
                  }}
                />
              </ContentContainer>
            ) : (
              <ContentContainer>
                <SubHeadContainer className="flex-column">
                  <LeftHead className="space-btwn">
                    <TitleHead>
                      <IconImg
                        onClick={() => handleBack()}
                        src={require("../../../src/assets/admin/images/back_arrow.png")}
                      />
                      <SubHeadTitle>Query Detail</SubHeadTitle>
                    </TitleHead>
                    {/* {!USER_ROLES?.includes(userroles) &&
                      pageType?.toLowerCase()?.trim().replace(/\s+/g, "") ===
                      "quotepending"  || params!==null &&( */}
                    {isEditMode && !USER_ROLES?.includes(userroles) && searchQueryData?.Quote_status !== "Confirmation Awaited" && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => newsearchFormRef.current?.submitForm()}
                          className="text-primary border border-primary hover:bg-primary hover:text-white font-medium rounded-lg text-sm px-4 py-3 text-center flex items-center justify-center gap-2 shadow-searchbox"
                        >
                          <span>Search Rate</span>
                        </button>
                        <button
                          onClick={() => semd_manualQuote()}
                          type="button"
                          className="text-white bg-primary hover:bg-secondary font-medium rounded-lg text-sm px-4 py-3 text-center flex items-center justify-center gap-2 shadow-searchbox"
                        >
                          <span>Send Manual Quote</span>
                        </button>
                      </div>
                    )}
                  </LeftHead>
                </SubHeadContainer>

                {searchQueryData?.cargoType === "Ocean Cargo" ? (
                  <ShipmentRateForm
                    ref={newsearchFormRef}
                    type={isEditMode ? "kam_admin_edit" : "kam_admin"}
                    searchBy={searchQueryData?.cargoType}
                    searchFormData={{
                      searchquery: searchQueryData,
                    }}
                  />
                ) : (
                  <NewsearchForm
                    ref={newsearchFormRef}
                    type={isEditMode ? "kam_admin_edit" : "kam_admin"}
                    searchBy={searchQueryData?.cargoType || "Air Cargo"}
                    searchFormData={{
                      searchquery: searchQueryData,
                    }}
                  />
                )}

              </ContentContainer>
            )}

            {/* KAM/admin view of a Confirmation Awaited query — this branch
                (pageType === "Confirmation Awaited") is what the eye icon
                actually opens for KAM, not the pageType === "view" branch
                above. markupDetails rides along on searchQueryData already,
                no separate fetch needed. */}
            {!USER_ROLES?.includes(userroles) &&
              searchQueryData?.Quote_status === "Confirmation Awaited" &&
              searchQueryData?.markupDetails?.markup != null && (
                <ContentContainer>
                  <div className="flex items-center gap-2 px-1">
                    <span className="text-xs font-bold text-gray-600 uppercase tracking-wide">
                      Markup
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {searchQueryData.markupDetails.markup}%
                    </span>
                  </div>
                </ContentContainer>
              )}

            {manualQuoteQueryData != null && (
              <>
                <ContentContainer>
                  {!USER_ROLES?.includes(userroles) &&
                    pageType === "Manual Quoted" && (
                      <SubHeadContainer className="flex-column">
                        <div className="item-center w-full 2xl:flex xl:flex lg:flex md:flex-none justify-between ">
                          <TitleHead>
                            <SubHeadTitle className="m-0"> Manual Quotation</SubHeadTitle>
                          </TitleHead>



                          {manualQuoteQueryData?.nodeData?.expiryTime && (
                            <div className="datettmain " >


                              <div className="datett flex items-center">





                                <p className="mr-1 text-xs font-bold">Quote Expiry : </p>
                                <small>
                                  {moment(
                                    manualQuoteQueryData?.nodeData?.expiryTime
                                  )
                                    .utcOffset("+05:30")
                                    .format("DD MMM YYYY, h:mm A")}{" "}
                                  IST
                                </small>

                              </div>
                            </div>

                          )}
                        </div>

                        <hr className="w-full" />

                      </SubHeadContainer>
                    )}
                  {pageType == "Quote Available" ? (
                    <NewRateAvailableList
                      props={searchQueryData}
                      // role_type={USER_ROLES?.includes(userroles)}
                      nodeData={manualQuoteQueryData?.nodeData}/>
                  ) : pageType?.toLowerCase()?.trim().replace(/\s+/g, "") ===
                    "quotepending" && !USER_ROLES?.includes(userroles) ? (
                    <></>
                  ) : (
                    <NewRateAvailableList
                      props={searchQueryData || params}
                      role_type={USER_ROLES?.includes(userroles)}
                      nodeData={manualQuoteQueryData?.nodeData}
                    />
                  )}
                </ContentContainer>
              </>
            )}
          </>
        ) : null}
        {/* The old inline "Booking Detail" branch (pageType bookingconfirmed /
            bookingcancelled, which rendered AirCargoDetails or
            OceanCargoDetails right here) is gone. Every role now opens a
            confirmed/cancelled booking at its own URL instead — air at
            /booking-history/air-booking-details, ocean at
            /booking-history/ocean-cargo-booking-details — so the customer and
            the KAM see the same page, and the link survives a refresh and can
            be shared. See handleAirBookingView / handleOceanBookingView, which
            the eye button routes to before ever reaching handleSeachFormdata. */}

        {/* ── Ocean "Booking Confirmed" documents popup (KAM/admin) ──
            Opened by the file icon in the Ocean actions column. Deliberately
            sticky: `maskClosable={false}` stops an outside click from closing
            it and `keyboard={false}` stops Esc, so the × in the header is the
            only way out. antd's own × is dropped (`closable={false}`) in favour
            of one inside the custom title row. */}
        <Modal
          open={oceanDocRecord != null}
          centered
          // Wide enough for the BL grid inside: its five columns need ~700px,
          // which at 720 left the row-action column behind a scrollbar.
          // `max-width: 94vw` keeps it on-screen on smaller displays.
          width={920}
          style={{ maxWidth: "94vw" }}
          closable={false}
          maskClosable={false}
          keyboard={false}
          footer={null}
          // Nothing typed into the BL grid may survive a close: rows added but
          // not saved are edits the KAM abandoned, so the next open has to
          // re-seed from the stored blDetails. The body is already gated on
          // `oceanDocRecord` (which the close handlers null out), so this makes
          // that guarantee explicit rather than a side effect of the guard.
          destroyOnClose
          onCancel={() => setOceanDocRecord(null)}
          title={
            <div className="flex items-center justify-between gap-3 pr-1">
              <span className="text-base font-semibold text-gray-900">
                Bill of Landing List
              </span>
              <button
                type="button"
                title="Close"
                aria-label="Close"
                onClick={() => setOceanDocRecord(null)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 rounded-full hover:bg-gray-100 hover:text-gray-700 transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          }
        >
          {oceanDocRecord && (
            <div className="pt-1">
              <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
                <div>
                  <span className="block text-xs font-bold text-gray-500 uppercase tracking-wide">
                    Route
                  </span>
                  <span className="text-gray-900">
                    {oceanDocRecord?.originPort?.originPort || "—"} →{" "}
                    {oceanDocRecord?.destinationPort?.destinationPort || "—"}
                  </span>
                </div>
                <div>
                  <span className="block text-xs font-bold text-gray-500 uppercase tracking-wide">
                    Mode
                  </span>
                  <span className="text-gray-900">
                    {oceanDocRecord?.mode || "—"}
                  </span>
                </div>
                <div>
                  <span className="block text-xs font-bold text-gray-500 uppercase tracking-wide">
                    Customer
                  </span>
                  <span className="text-gray-900">
                    {oceanDocRecord?.userDetails?.full_name || "—"}
                  </span>
                </div>
                  <div>
                  <span className="block text-xs font-bold text-gray-500 uppercase tracking-wide">
                    MBL Number
                  </span>
                  <span className="text-gray-900">
                    {oceanDocRecord?.mblNumber || "—"}
                  </span>
                </div>
              </div>

              {/* Editable per-BL grid, seeded from the blDetails array the
                  bookinglist projection returns on the row. Keyed by query id
                  so switching rows remounts with that booking's own rows
                  rather than carrying over unsaved edits from the last one. */}
              <div className="mt-4">
                <BlDetailsEditor
                  key={oceanDocRecord?._id}
                  queryId={oceanDocRecord?._id}
                  blDetails={oceanDocRecord?.blDetails}
                  onSaved={handleBlDetailsSaved}
                />
              </div>
            </div>
          )}
        </Modal>

        {/* Outlives the popup above, which closes itself on a successful BL
            save — a container mounted inside it would unmount before the
            success toast could be read. */}
        <ToastContainer />

        <VerifyEmailModal
          open={showVerifyModal}
          onClose={() => {
            pendingOceanOpenRef.current = null;
            setShowVerifyModal(false);
          }}
          // Verified: open the ocean rates that were held back.
          onVerified={() => {
            setShowVerifyModal(false);
            const open = pendingOceanOpenRef.current;
            pendingOceanOpenRef.current = null;
            if (open) {
              setOpeningRates(true);
              open();
            }
          }}
          defaultEmail={currentUser?.email || currentUser?.unverified_email}
          userId={currentUser?.id}
        />
      </MainContainer>
    </div>
  );
}

export default Queries;
